import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Card, EmptyState, Input, PageHeader, Spinner } from "@empite/ui-components";
import type { Creation } from "../marketplace-client";
import { useBrowse } from "../marketplace-hooks";
import { ErrorAlert, FilePlaceholder, when } from "./common";

export interface BrowseSearch {
  /** The search term, in the URL so a result list can be linked to. */
  q?: string;
}

/**
 * Browse — the marketplace's front page, and the one screen that works
 * signed out.
 *
 * The list is whatever the server says is visible. There is no client-side
 * filter on status or moderation state anywhere in this portal: hiding a row
 * in the browser would mean the row had already been sent, and visibility is
 * one rule living in internal/marketplace/service.go:visibleTo.
 */
export function Browse({ search }: { search: BrowseSearch }) {
  const navigate = useNavigate();
  const query = search.q ?? "";
  const [term, setTerm] = useState(query);
  const creations = useBrowse(query);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // The term goes into the URL and the URL drives the query — one source of
    // truth, so a back button lands on the results it left.
    void navigate({ to: "/", search: term.trim() === "" ? {} : { q: term.trim() } });
  };

  return (
    <>
      <PageHeader
        title="Marketplace"
        description="Everything the suite's users have published. Files are stored and served exactly as they were uploaded."
      />
      <form className="esm-search" onSubmit={submit} role="search">
        <Input
          aria-label="Search creations"
          placeholder="Search by title or summary"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        <Button type="submit">Search</Button>
        {query !== "" ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setTerm("");
              void navigate({ to: "/", search: {} });
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      <ErrorAlert error={creations.error} title="We could not load the marketplace" />
      {creations.isPending ? (
        <Spinner label="Loading creations" />
      ) : (creations.data ?? []).length === 0 ? (
        <EmptyState title={query === "" ? "Nothing published yet" : `Nothing matches “${query}”`}>
          <p>
            {query === ""
              ? "Be the first: upload a file and publish it."
              : "Try a shorter search term."}
          </p>
        </EmptyState>
      ) : (
        <div className="esm-creation-grid">
          {(creations.data ?? []).map((creation) => (
            <CreationTile key={creation.id} creation={creation} />
          ))}
        </div>
      )}
    </>
  );
}

function CreationTile({ creation }: { creation: Creation }) {
  return (
    <Card
      title={
        <Link to="/creations/$creationID" params={{ creationID: creation.id }}>
          {creation.title}
        </Link>
      }
    >
      <FilePlaceholder tile />
      <p>{creation.summary}</p>
      <p className="esm-creation__meta">
        {creation.author_display_name ? `by ${creation.author_display_name} · ` : ""}
        published {when(creation.published_at)}
      </p>
      <p className="esm-creation__counts">
        <span>{creation.like_count} likes</span>
        <span>{creation.comment_count} comments</span>
        <span>{creation.download_count} downloads</span>
      </p>
    </Card>
  );
}
